<template>
  <div v-if="visible" class="h-screen w-screen bg-black/25 backdrop-blur inset-0 fixed">
    <div class="flex flex-col justify-center items-center w-full h-full">
      <div class="max-w-lg w-full p-6">
        <div class="bg-gray-100 bg-border p-2 rounded-lg mb-4 flex items-center justify-between dark:bg-gray-900">
          <div>
            <segment-field
              v-model="_type"
              :options="[
                { label: $t('general.receipt'), value: 'thermal' },
                { label: $t('general.a4'), value: 'standard' }
              ]"
              class="border"
            />
          </div>

          <div class="flex items-center space-x-2">
            <o-button outline flat @click="visible = false">
              {{ $t('general.cancel') }}
            </o-button>
            <o-button variant="primary" @click="handlePrint">
              {{ $t('general.print') }}
            </o-button>
          </div>
        </div>
      </div>

      <div class="flex-grow flex-shrink w-full h-10">
        <template v-if="_type === 'thermal'">
          <div class="w-full h-full max-w-xs mx-auto">
            <o-scroll>
              <div id="receipt-content" class="receipt text-left w-full text-sm">
                <div class="bg-white p-4 overflow-auto dark:bg-gray-900">
                  <div class="text-center">
                    <img v-if="logo" :src="logo" :alt="title" class="mb-3 h-10 inline-block">
                    <h2 class="text-base font-semibold" v-html="header || title" />
                    <p class="hidden">
                      {{ address }}
                    </p>
                  </div>
                  <div class="flex justify-between mt-6 text-xs">
                    <div>
                      {{ $t('components.order.actions.printReceipt.no', { number: order.orderNumber }) }}
                    </div>
                    <div>
                      {{ order.dateLabel }}
                    </div>
                  </div>

                  <template v-for="(group, key) in order.data.items" :key="key">
                    <div v-if="group.length" class="mt-6">
                      <h4 class="font-semibold uppercase text-sm border-b pb-2 mb-2">
                        {{ key }}
                      </h4>
                      <table class="w-full text-xs">
                        <thead>
                          <tr>
                            <th class="py-1 text-left">
                              {{ $t('general.item') }}
                            </th>
                            <th class="py-1 w-3/12 text-right">
                              {{ $t('general.price') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, index) in group" :key="key + '-' + index">
                            <td class="py-2 text-left">
                              {{ item.displayLabel }}
                            </td>
                            <td class="py-2 text-right">
                              <template v-if="item.isCharge && item.finalPriceLabel">
                                {{ item.finalPriceLabel }} ({{ item.priceLabel }})
                              </template>
                              <template v-else>
                                {{ item.priceLabel }}
                              </template>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </template>
                  <hr class="my-2">
                  <div class="mt-3">
                    <div class="text-xs flex font-semibold mb-1">
                      <div class="flex-grow uppercase">
                        {{ $t('general.subtotal') }}
                      </div>
                      <div>
                        {{ subtotal }}
                      </div>
                    </div>

                    <div v-if="hasCharges" class="text-xs flex font-semibold mb-1">
                      <div class="flex-grow uppercase">
                        {{ $t('order.charges') }}
                      </div>
                      <div>
                        {{ charges }}
                      </div>
                    </div>

                    <div v-for="(tax, key) in order.data.taxes" :key="tax.idTax" class="text-xs flex font-semibold mb-1">
                      <div class="flex-grow">
                        {{ tax.name }}
                      </div>
                      <div>
                        {{ $currency.format(order.data.totalsForTaxes[key]) }}
                      </div>
                    </div>

                    <hr class="my-2">

                    <div class="text-xs flex font-semibold mb-1">
                      <div class="flex-grow uppercase">
                        {{ $t('general.total') }}
                      </div>
                      <div class="text-base">
                        {{ total }}
                      </div>
                    </div>

                    <div v-if="discounts" class="text-xs flex font-semibold mb-1">
                      <div class="flex-grow uppercase">
                        {{ $t('general.discounts') }}
                      </div>
                      <div>
                        {{ discounts }}
                      </div>
                    </div>

                    <div v-if="hasTip" class="text-xs flex font-semibold mb-1">
                      <div class="flex-grow uppercase">
                        {{ $t('order.tip') }}
                      </div>
                      <div>
                        {{ tip }}
                      </div>
                    </div>

                    <div class="text-xs flex font-semibold mb-1">
                      <div class="flex-grow uppercase">
                        {{ $t('general.payments') }}
                      </div>
                      <div>
                        {{ payments }}
                      </div>
                    </div>

                    <div v-if="hasRemaining" class="text-xs flex font-semibold mb-1 mt-2 pt-2 border-t">
                      <div class="flex-grow uppercase">
                        {{ $t('order.remainder') }}
                      </div>
                      <div class="text-base">
                        {{ remainder }}
                      </div>
                    </div>

                    <div v-if="$features.get('loyalty') && hasLoyalty" class="text-xs flex font-semibold mb-1 mt-2 pt-2 border-t">
                      <div class="flex-grow uppercase">
                        {{ $t('general.loyaltyPoints') }}
                      </div>
                      <div>
                        {{ loyalty }}
                      </div>
                    </div>
                  </div>

                  <div class="text-center mt-10 text-xs">
                    <p v-if="hasEmployee">
                      {{ $t('components.order.actions.printReceipt.employee', { name: order.employee.displayName }) }}
                    </p>
                    <p v-if="hasCustomer" class="mt-1">
                      {{ $t('components.order.actions.printReceipt.customer', { name: order.customer.displayName }) }}<br>
                      <span v-if="order.customer.addressLabel">{{ order.customer.addressLabel }}</span>
                    </p>

                    <div v-if="customFields" class="mt-2 pt-2 border-t">
                      <template v-for="customField in customFields" :key="customField.idCustomField">
                        <p v-if="customField.customerVisible">
                          {{ $t('general.colonSeparator', { label1: customField.name, label2: customField.valueLabel }) }}
                        </p>
                      </template>
                    </div>

                    <div v-if="hasNotes" class="mt-2 pt-2 border-t">
                      <p>
                        {{ $t('components.order.actions.printReceipt.notes', { notes: order.notes }) }}
                      </p>
                    </div>

                    <p v-if="orderTaxDetails" class="mt-2 pt-2 border-t" v-html="orderTaxDetails" />
                  </div>

                  <div v-if="footer" class="mt-6 text-sm text-center">
                    <p v-html="footer" />
                  </div>
                </div>
              </div>
            </o-scroll>
          </div>
        </template>

        <template v-if="_type === 'standard'">
          <div class="w-full h-full  max-w-4xl mx-auto">
            <o-scroll>
              <div id="receipt-content" class="bg-white text-left w-full text-sm p-12 dark:bg-gray-900">
                <div class="flex items-start justify-between mb-12">
                  <div>
                    <img v-if="logo" :src="logo" :alt="title" class="max-w-[200px] inline-block">
                  </div>

                  <div class="text-right max-w-2xs">
                    <p class="" v-html="header || title" />
                    <p class="hidden">
                      {{ address }}
                    </p>

                    <div class="mt-4">
                      <p v-if="location.phone">
                        {{ location.phone }}
                      </p>
                      <p v-if="location.email">
                        {{ location.email }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="flex items-start justify-between mb-20">
                  <div>
                    <div v-if="hasCustomer">
                      <p class="font-semibold mb-3 mt-1">
                        {{ $t('general.customer') }}
                      </p>

                      <p>{{ order.customer.displayName }}</p>
                      <p v-if="order.customer.addressLabel">
                        {{ order.customer.addressLabel }}
                      </p>
                      <p v-if="order.customer.email">
                        {{ order.customer.email }}
                      </p>
                      <p v-if="order.customer.phone">
                        {{ order.customer.phone }}
                      </p>

                      <div v-if="customFields" class="mt-4">
                        <template v-for="customField in customFields" :key="customField.idCustomField">
                          <p v-if="customField.customerVisible">
                            {{ $t('general.colonSeparator', { label1: customField.name, label2: customField.valueLabel }) }}
                          </p>
                        </template>
                      </div>
                    </div>
                  </div>

                  <div class="text-right max-w-2xs">
                    <div>
                      <p class="font-semibold mb-4 text-2xl tracking-tighter leading-none">
                        {{ $t('general.receipt') }}
                      </p>
                      <div class="mb-3">
                        <p class="uppercase font-semibold">
                          {{ $t('components.order.actions.printReceipt.receiptNo') }}
                        </p>
                        <p class="text-gray-800 dark:text-gray-100">
                          {{ order.orderNumber }}
                        </p>
                      </div>
                      <div class="mb-3">
                        <p class="uppercase font-semibold">
                          {{ $t('general.date') }}
                        </p>
                        <p class="text-gray-800 dark:text-gray-100">
                          {{ order.dateLabel }}
                        </p>
                      </div>
                      <div v-if="order.employee">
                        <p class="uppercase font-semibold">
                          {{ $t('general.employee') }}
                        </p>
                        <p class="text-gray-800 dark:text-gray-100">
                          {{ order.employee.displayName }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <template v-for="(group, key) in order.data.items" :key="key">
                  <div v-if="group.length" class="mt-6">
                    <h4 class="font-semibold uppercase border-b pb-2 mb-2">
                      {{ key }}
                    </h4>
                    <table class="w-full">
                      <thead>
                        <tr>
                          <th class="py-1 text-left">
                            {{ $t('general.item') }}
                          </th>
                          <th class="py-1 w-3/12 text-right">
                            {{ $t('general.price') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in group" :key="key + '-' + index">
                          <td class="py-2 text-left">
                            {{ item.displayLabel }}
                          </td>
                          <td class="py-2 text-right">
                            <template v-if="item.isCharge">
                              {{ item.finalPriceLabel }} ({{ item.priceLabel }})
                            </template>
                            <template v-else>
                              {{ item.priceLabel }}
                            </template>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>

                <div class="mt-12 flex items-start justify-end">
                  <div v-if="hasNotes" class="w-1/2 pr-4">
                    <p class="font-semibold uppercase mb-3">
                      {{ $t('general.notes') }}
                    </p>

                    <hr class="my-2">

                    <p class="text-gray-800 dark:text-gray-100">
                      {{ order.notes }}
                    </p>
                  </div>

                  <div class="w-1/2 pl-4">
                    <div class="flex font-semibold mb-3">
                      <div class="flex-grow uppercase">
                        {{ $t('general.subtotal') }}
                      </div>
                      <div>
                        {{ subtotal }}
                      </div>
                    </div>

                    <div v-if="hasCharges" class="flex font-semibold mb-3">
                      <div class="flex-grow uppercase">
                        {{ $t('order.charges') }}
                      </div>
                      <div>
                        {{ charges }}
                      </div>
                    </div>

                    <div v-for="(tax, key) in order.data.taxes" :key="tax.idTax" class="flex font-semibold mb-3">
                      <div class="flex-grow">
                        {{ tax.name }}
                      </div>
                      <div>
                        {{ $currency.format(order.data.totalsForTaxes[key]) }}
                      </div>
                    </div>

                    <hr class="my-2">

                    <div class="flex font-semibold mb-3">
                      <div class="flex-grow uppercase">
                        {{ $t('general.total') }}
                      </div>
                      <div>
                        {{ total }}
                      </div>
                    </div>

                    <div v-if="discounts" class="flex font-semibold mb-3">
                      <div class="flex-grow uppercase">
                        {{ $t('general.discounts') }}
                      </div>
                      <div>
                        {{ discounts }}
                      </div>
                    </div>

                    <div v-if="hasTip" class="flex font-semibold mb-3">
                      <div class="flex-grow uppercase">
                        {{ $t('order.tip') }}
                      </div>
                      <div>
                        {{ tip }}
                      </div>
                    </div>

                    <div class="flex font-semibold mb-3">
                      <div class="flex-grow uppercase">
                        {{ $t('general.payments') }}
                      </div>
                      <div>
                        {{ payments }}
                      </div>
                    </div>

                    <div v-if="hasRemaining" class="flex font-semibold mb-3 mt-2 pt-2 border-t">
                      <div class="flex-grow uppercase">
                        {{ $t('order.remainder') }}
                      </div>
                      <div class="text-base">
                        {{ remainder }}
                      </div>
                    </div>

                    <div v-if="$features.get('loyalty') && hasLoyalty" class="flex font-semibold mb-3 mt-2 pt-2 border-t">
                      <div class="flex-grow uppercase">
                        {{ $t('general.loyaltyPoints') }}
                      </div>
                      <div>
                        {{ loyalty }}
                      </div>
                    </div>
                  </div>
                </div>

                <p v-if="orderTaxDetails" class="mt-4 text-center" v-html="orderTaxDetails" />

                <div v-if="footer" class="mt-12 text-sm text-center text-gray-700 dark:text-gray-200">
                  <p v-html="footer" />
                </div>
              </div>
            </o-scroll>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  SegmentField,
} from '@/components/fields'

import { useUIStore } from '~/stores/useUIStore'

interface Props {
  order: Record<string, any>
}

const props = defineProps<Props>()

const { $currency, $auth, $print } = useNuxtApp()

const uiStore = useUIStore()

const visible = ref(false)
const items = ref<any[]>([])

const _type = computed({
  get () {
    return uiStore.order.print || 'thermal'
  },
  set (value: string) {
    uiStore.setOrder({ key: 'print', value })
  },
})

const currencySymbol = computed(() => props.order.data.currencySymbol)

const location = computed(() => ($auth as any).info.location)

const title = computed(() => location.value.name)

const header = computed(() => {
  return location.value.receiptHeader?.replace(/(?:\r\n|\r|\n)/g, '<br />')
})

const footer = computed(() => {
  return location.value.receiptFooter?.replace(/(?:\r\n|\r|\n)/g, '<br />')
})

const logo = computed(() => location.value.images?.small)

const address = computed(() => location.value.address)

const subtotal = computed(() => $currency.format(props.order.data.totals.subtotal))

const charges = computed(() => $currency.format(props.order.data.totals.charges))

const taxes = computed(() => $currency.format(props.order.data.totals.taxes))

const total = computed(() => $currency.format(props.order.data.totals.total))

const tip = computed(() => $currency.format(Number(props.order.data.totals.tip)))

const payments = computed(() => $currency.format(props.order.data.totals.payments))

const remainder = computed(() => $currency.format(props.order.data.totals.remainder))

const loyalty = computed(() => props.order.data.totals.loyaltyPointsEarned)

const customFields = computed(() => props.order.customFields)

const hasCharges = computed(() => props.order.data.items.charges.length)

const hasRemaining = computed(() => props.order.hasRemaining)

const hasLoyalty = computed(() => props.order.data.totals.loyaltyPointsEarned)

const hasEmployee = computed(() => !!props.order.employee)

const hasCustomer = computed(() => !!props.order.customer)

const hasTip = computed(() => props.order.data.totals.tip > 0)

const hasNotes = computed(() => !!props.order.notes)

const isClosed = computed(() => props.order.closed)

const orderTaxDetails = computed(() => {
  return location.value.orderTaxDetails?.replace(/(?:\r\n|\r|\n)/g, '<br />')
})

const customerAddress = computed(() => {
  return `${props.order.customer?.address}<br />${props.order.customer?.address2 ? `${props.order.customer?.address2}<br />` : ''}${props.order.customer?.city} ${props.order.customer?.region} ${props.order.customer?.postcode}<br />${props.order.customer?.country}`
})

const discounts = computed(() => {
  const discountsVal = props.order.data.totals.discounts
  return discountsVal ? $currency.format(discountsVal) : null
})

function open () {
  visible.value = true
}

function handlePrint () {
  const el = document.getElementById('receipt-content')
  if (!el) return
  $print(el.innerHTML)
}

defineExpose({ open })
</script>

<style lang="scss">
$triangle-size: 4px; // Define once and use everywhere

@mixin receipt-background($color) {
  $stop1: $triangle-size * 1.42;
  $stop2: $triangle-size * 0.7;
  $stop1r: $stop1 + 0.01;
  $stop2r: $stop2 + 0.01;

  background:
    linear-gradient(135deg, transparent $stop1, $color $stop1r) top left,
    linear-gradient(45deg, $color $stop2, transparent $stop2r) top left,
    linear-gradient(135deg, $color $stop2, transparent $stop2r) bottom left,
    linear-gradient(45deg, transparent $stop1, $color $stop1r) bottom left;
  background-repeat: repeat-x;
  background-size: ($triangle-size * 2) $triangle-size;
  padding: $triangle-size 0;
}

.receipt {
  @include receipt-background(white);

  &-list {
    background-color: white;
  }
}

.dark .receipt {
  @include receipt-background(var(--color-gray-900));

  &-list {
    background-color: var(--color-gray-900);
  }
}

</style>
